import { useEffect, useState } from 'react';
import { api, download, openDocument } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { Link, navigate, setQuery, useLocation } from '../../lib/router';
import type { TokenBalance } from '../../lib/types';
import {
  AsyncButton, Badge, Button, Card, Checkbox, CopyField, DataTable, Empty, ErrorBox, Field, Input, Loading, Modal, PageHeader, Select, Tabs, Textarea, cx, errorText, fieldErrors, useToast,
} from '../../ui/components';
import { ChatBox } from '../public/chat';
import { TokenChips } from './planner';

export function BookingsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const scope = query.get('scope') === 'past' ? 'past' : 'upcoming';
  const list = useApi(() => api<any[]>(`${T(gym.slug)}/me/bookings`, { query: { scope } }), [scope]);
  const standing = useApi(() => api<any[]>(`${T(gym.slug)}/me/standing-bookings`), []);
  useEffect(() => { if (query.get('paid')) { toast.ok(i18n.t('bookings.payment_received')); setQuery({ paid: null }); } }, []);
  const now = new Date().toISOString();
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.bookings')} actions={<Button onClick={() => navigate(`${gym.base}/planner`)}>{i18n.t('bookings.book_more')}</Button>} />
      <Tabs value={scope} onChange={(v) => setQuery({ scope: v === 'upcoming' ? null : v })} tabs={[{ id: 'upcoming', label: i18n.t('bookings.upcoming') }, { id: 'past', label: i18n.t('bookings.past') }]} />
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : !list.data ? <Loading /> : list.data.length === 0 ? <Empty icon="🗓">{i18n.t('bookings.none')}</Empty> : (
        <div className="stack-sm">
          {list.data.map((b) => {
            const late = now > b.cancelFreeUntil;
            const cancellable = scope === 'upcoming' && ['booked', 'waitlisted'].includes(b.status) && b.startsAt > now;
            return (
              <div key={b.id} className="booking-row" style={{ borderLeftColor: b.lesson.color ?? 'var(--brand)' }}>
                <div>
                  <strong>{b.lesson.name}</strong> <Badge value={b.sessionStatus === 'cancelled' ? 'cancelled' : b.status} />
                  <div className="muted small">{i18n.date(b.startsAt, { weekday: 'long', day: 'numeric', month: 'long' })} · {i18n.time(b.startsAt)}–{i18n.time(b.endsAt)} · {b.location}{b.trainer ? ` · ${b.trainer}` : ''}</div>
                  {b.status === 'waitlisted' && <div className="small">{i18n.t('planner.waitlist_position', { position: b.waitlistPosition })}</div>}
                  {b.sessionStatus === 'cancelled' && b.cancelReason && <div className="small warn">{b.cancelReason}</div>}
                  {cancellable && b.status === 'booked' && <div className={late ? 'small warn' : 'small muted'}>{late ? i18n.t('planner.late_warning') : i18n.t('planner.free_until', { time: i18n.dateTime(b.cancelFreeUntil) })}</div>}
                </div>
                {cancellable && b.status === 'booked' && (
                  <AsyncButton variant="ghost" size="sm" onClick={async () => {
                    await api(`${T(gym.slug)}/me/standing-bookings`, { body: { sessionId: b.sessionId } });
                    toast.ok(i18n.t('bookings.repeating'));
                    standing.reload();
                  }}>{i18n.t('bookings.repeat')}</AsyncButton>
                )}
                {cancellable && (
                  <AsyncButton variant="secondary" size="sm" confirm={late && b.status === 'booked' ? i18n.t('planner.late_confirm') : undefined}
                    onClick={async () => { await api(`${T(gym.slug)}/bookings/${b.id}/cancel`, { body: {} }); list.reload(); }}>
                    {i18n.t(b.status === 'waitlisted' ? 'planner.leave_waitlist' : 'common.cancel')}
                  </AsyncButton>
                )}
              </div>
            );
          })}
        </div>
      )}
      {(standing.data ?? []).length > 0 && (
        <Card title={i18n.t('bookings.standing_title')}>
          <p className="small muted">{i18n.t('bookings.standing_help')}</p>
          {standing.data!.map((x) => (
            <div key={x.id} className="row between wrap">
              <div>
                <strong>{x.lesson}</strong>
                <div className="small muted">{i18n.t('bookings.every_week', { day: i18n.day(`2024-01-0${Number(x.weekday) + 7}`, { weekday: 'long' }), time: x.start_time })}{x.location ? ` · ${x.location}` : ''}</div>
                {x.last_problem && <div className="small warn">{i18n.t(`error.${x.last_problem}`)}</div>}
              </div>
              <AsyncButton size="sm" variant="secondary" onClick={async () => {
                await api(`${T(gym.slug)}/me/standing-bookings/${x.id}`, { method: 'DELETE' });
                toast.ok(i18n.t('bookings.stopped'));
                standing.reload();
              }}>{i18n.t('bookings.stop_repeat')}</AsyncButton>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

const HEALTH_QUESTIONS = ['heart_condition', 'chest_pain', 'dizziness', 'joint_problems', 'medication', 'pregnant'];
const DETAIL_FIELDS = ['first_name', 'name_prefix', 'last_name', 'preferred_name', 'date_of_birth', 'gender', 'phone', 'street', 'house_number', 'house_number_addition', 'postal_code', 'city', 'country'];

export function ProfilePage() {
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const tab = query.get('tab') ?? 'details';
  const me = useApi(() => api(`${T(gym.slug)}/me`), []);
  if (me.error) return <div className="page"><ErrorBox error={me.error} onRetry={me.reload} /></div>;
  if (!me.data) return <Loading />;
  const person = me.data.person;
  return (
    <div className="page">
      <PageHeader title={`${person.preferred_name || person.first_name} ${person.last_name === '-' ? '' : person.last_name}`} subtitle={person.member_number ? `${i18n.field('member_number')}: ${person.member_number}` : undefined} />
      <Tabs value={tab} onChange={(v) => setQuery({ tab: v })} tabs={['details', 'memberships', 'health', 'privacy', 'payments', 'documents'].map((id) => ({ id, label: i18n.t(`profile.tab_${id}`) }))} />
      {tab === 'details' && <DetailsTab person={person} onSaved={me.reload} />}
      {tab === 'memberships' && <MembershipsTab memberships={me.data.memberships} tokens={me.data.tokens} onChanged={me.reload} />}
      {tab === 'health' && <HealthTab />}
      {tab === 'privacy' && <PrivacyTab person={person} onSaved={me.reload} />}
      {tab === 'payments' && <PaymentsTab />}
      {tab === 'documents' && <DocumentsTab />}
    </div>
  );
}

function DetailsTab({ person, onSaved }: { person: Record<string, any>; onSaved(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [v, setV] = useState<Record<string, any>>(() => Object.fromEntries(DETAIL_FIELDS.map((k) => [k, person[k] ?? ''])));
  const [errors, setErrors] = useState<Record<string, string>>({});
  return (
    <Card>
      <div className="grid-2">
        {DETAIL_FIELDS.map((k) => (
          <Field key={k} label={i18n.field(k)} error={errors[k]}>
            {k === 'gender' ? (
              <Select value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} placeholder="—" options={['female', 'male', 'non_binary', 'other', 'undisclosed'].map((g) => ({ value: g, label: i18n.option(g) }))} />
            ) : (
              <Input type={k === 'date_of_birth' ? 'date' : k === 'phone' ? 'tel' : 'text'} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
            )}
          </Field>
        ))}
        <Field label={i18n.field('email')} hint={i18n.t('profile.email_hint')}><Input value={person.email ?? ''} disabled /></Field>
      </div>
      <AsyncButton onClick={async () => {
        setErrors({});
        try { await api(`${T(gym.slug)}/me`, { method: 'PATCH', body: v }); toast.ok(i18n.t('common.saved')); onSaved(); } catch (e) { setErrors(fieldErrors(e)); throw e; }
      }}>{i18n.t('common.save')}</AsyncButton>
    </Card>
  );
}

function MembershipsTab({ memberships, tokens, onChanged }: { memberships: any[]; tokens: TokenBalance[]; onChanged(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [cancel, setCancel] = useState<any | null>(null);
  const [keepOpen, setKeepOpen] = useState(true);
  const alternatives = useApi(cancel ? () => api(`${T(gym.slug)}/me/memberships/${cancel.id}/alternatives`) : null, [cancel?.id]);
  const [pause, setPause] = useState<any | null>(null);
  const [reason, setReason] = useState('');
  const [dates, setDates] = useState({ startDate: '', endDate: '' });
  return (
    <div className="stack">
      <Card title={i18n.t('profile.tokens')}>
        <TokenChips tokens={tokens} />
        {tokens.some((t) => t.batches.length > 1) && (
          <details className="details"><summary>{i18n.t('profile.token_details')}</summary>
            <DataTable rows={tokens.flatMap((t) => t.batches.map((b) => ({ ...b, name: t.name })))} columns={[
              { key: 'name', label: i18n.t('planner.token') },
              { key: 'remaining', label: i18n.field('remaining') },
              { key: 'expiresAt', label: i18n.field('expires_at'), render: (b) => (b.expiresAt ? i18n.dateTime(b.expiresAt) : i18n.t('tokens.no_expiry')) },
              { key: 'source', label: i18n.field('source'), render: (b) => i18n.option(b.source) },
            ]} />
          </details>
        )}
      </Card>
      {memberships.length === 0 && <Empty>{i18n.t('profile.no_membership')} <Link to={`${gym.base}/join`}>{i18n.t('public.become_member')}</Link></Empty>}
      {memberships.map((m) => (
        <Card key={m.id} title={<>{m.plan} <Badge value={m.status} /></>}>
          <dl className="dl">
            <div><dt>{i18n.field('price_cents')}</dt><dd>{i18n.money(m.priceCents)}{m.billingInterval !== 'none' ? ` / ${i18n.t(`interval.${m.billingInterval}`)}` : ''}</dd></div>
            <div><dt>{i18n.field('start_date')}</dt><dd>{m.startDate ? i18n.day(m.startDate) : '—'}</dd></div>
            {m.minTermEnd && <div><dt>{i18n.field('min_term_end')}</dt><dd>{i18n.day(m.minTermEnd)}</dd></div>}
            {m.cancelEffectiveDate && <div><dt>{i18n.t('profile.ends_on')}</dt><dd>{i18n.day(m.cancelEffectiveDate)}</dd></div>}
            {m.endDate && <div><dt>{i18n.field('end_date')}</dt><dd>{i18n.day(m.endDate)}</dd></div>}
            {m.pauseStart && <div><dt>{i18n.t('profile.paused')}</dt><dd>{i18n.day(m.pauseStart)} – {m.pauseEnd ? i18n.day(m.pauseEnd) : '…'}</dd></div>}
          </dl>
          <div className="row gap-sm wrap">
            {m.canCancel && <Button variant="secondary" onClick={() => { setKeepOpen(true); setCancel(m); }}>{i18n.t('profile.cancel_membership')}</Button>}
            {m.canPause && <Button variant="ghost" onClick={() => setPause(m)}>{i18n.t('profile.pause')}</Button>}
            {m.canWithdraw && (
              <AsyncButton variant="danger" confirm={i18n.t('profile.withdraw_confirm')} onClick={async () => {
                const r = await api(`${T(gym.slug)}/me/memberships/${m.id}/withdraw`, { body: { confirm: true } });
                toast.ok(i18n.t('profile.withdrawn', { amount: i18n.money(r.refundCents) }));
                onChanged();
              }}>{i18n.t('profile.withdraw', { date: i18n.day(m.withdrawUntil) })}</AsyncButton>
            )}
          </div>
        </Card>
      ))}
      {cancel && (
        <Modal title={i18n.t(keepOpen ? 'profile.before_you_go' : 'profile.cancel_membership')} onClose={() => { setCancel(null); setKeepOpen(true); }} footer={
          keepOpen ? <Button variant="ghost" onClick={() => setKeepOpen(false)}>{i18n.t('profile.continue_cancel')}</Button> : (
          <AsyncButton variant="danger" onClick={async () => {
            const r = await api(`${T(gym.slug)}/me/memberships/${cancel.id}/cancel`, { body: { reason, channel: 'web' } });
            toast.ok(i18n.t('profile.cancel_done', { date: i18n.day(r.effectiveDate) }));
            setCancel(null);
            onChanged();
          }}>{i18n.t('profile.confirm_cancel')}</AsyncButton>)
        }>
          {keepOpen ? (
            <div className="stack">
              <p>{i18n.t('profile.save_intro')}</p>
              {alternatives.data?.canPause && (
                <Card title={i18n.t('profile.pause')}>
                  <p className="small muted">{i18n.t('profile.save_pause')}</p>
                  <Button variant="secondary" onClick={() => { setPause(cancel); setCancel(null); }}>{i18n.t('profile.pause')}</Button>
                </Card>
              )}
              {(alternatives.data?.cheaperPlans ?? []).length > 0 && (
                <Card title={i18n.t('profile.save_cheaper')}>
                  {alternatives.data!.cheaperPlans.map((p: any) => (
                    <div key={p.id} className="row between wrap">
                      <div>
                        <strong>{p.name}</strong> · {i18n.money(p.priceCents)}{p.interval && p.interval !== 'none' ? ` / ${i18n.t(`interval.${p.interval}`)}` : ''}
                        {p.description && <div className="small muted">{p.description}</div>}
                      </div>
                      <AsyncButton size="sm" onClick={async () => {
                        const r = await api(`${T(gym.slug)}/me/memberships/${cancel.id}/change-plan`, { body: { planId: p.id } });
                        toast.ok(i18n.t('profile.plan_changed', { plan: r.plan, date: i18n.day(r.effectiveDate) }));
                        setCancel(null);
                        onChanged();
                      }}>{i18n.t('profile.switch_to')}</AsyncButton>
                    </div>
                  ))}
                </Card>
              )}
              <p className="small muted">{i18n.t('profile.save_help')}</p>
            </div>
          ) : (
            <>
              <p>{i18n.t('profile.cancel_explain', { days: cancel.noticeDays ?? 30 })}</p>
              <Field label={i18n.t('profile.cancel_reason')}><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
            </>
          )}
        </Modal>
      )}
      {pause && (
        <Modal title={i18n.t('profile.pause')} onClose={() => setPause(null)} footer={
          <AsyncButton onClick={async () => { await api(`${T(gym.slug)}/me/memberships/${pause.id}/pause`, { body: dates }); setPause(null); onChanged(); toast.ok(i18n.t('common.saved')); }}>{i18n.t('common.save')}</AsyncButton>
        }>
          <div className="grid-2">
            <Field label={i18n.field('start_date')}><Input type="date" value={dates.startDate} onChange={(e) => setDates({ ...dates, startDate: e.target.value })} /></Field>
            <Field label={i18n.field('end_date')}><Input type="date" value={dates.endDate} onChange={(e) => setDates({ ...dates, endDate: e.target.value })} /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

function HealthTab() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const h = useApi(() => api(`${T(gym.slug)}/me/health`), []);
  const [v, setV] = useState<any>(null);
  useEffect(() => { if (h.data) setV({ ...h.data, intake: h.data.intake ?? {}, consent: Boolean(h.data.consentAt) }); }, [h.data]);
  if (!v) return <Loading />;
  const contacts: any[] = v.emergencyContacts ?? [];
  const setContact = (i: number, patch: Record<string, string>): void => setV({ ...v, emergencyContacts: [0, 1].map((j) => (j === i ? { ...(contacts[j] ?? {}), ...patch } : contacts[j])).filter(Boolean) });
  return (
    <div className="stack">
      <Card title={i18n.t('profile.ice')}>
        <p className="small muted">{i18n.t('profile.ice_help')}</p>
        {[0, 1].map((i) => (
          <div className="grid-3" key={i}>
            <Field label={i18n.field('name')}><Input value={contacts[i]?.name ?? ''} onChange={(e) => setContact(i, { name: e.target.value })} /></Field>
            <Field label={i18n.t('health.relation')}><Input value={contacts[i]?.relation ?? ''} onChange={(e) => setContact(i, { relation: e.target.value })} /></Field>
            <Field label={i18n.field('phone')}><Input type="tel" value={contacts[i]?.phone ?? ''} onChange={(e) => setContact(i, { phone: e.target.value })} /></Field>
          </div>
        ))}
      </Card>
      <Card title={i18n.t('profile.health')}>
        <p className="small muted">{i18n.t('profile.health_help')}</p>
        <Checkbox checked={v.consent} onChange={(c) => setV({ ...v, consent: c })} label={i18n.t('consent.health')} />
        {v.consent && (
          <div className="stack-sm">
            {HEALTH_QUESTIONS.map((q) => <Checkbox key={q} label={i18n.t(`health.q_${q}`)} checked={Boolean(v.intake[q])} onChange={(c) => setV({ ...v, intake: { ...v.intake, [q]: c } })} />)}
            <Field label={i18n.t('trial.health_notes')}><Textarea rows={3} value={v.notes ?? ''} onChange={(e) => setV({ ...v, notes: e.target.value })} /></Field>
            <Checkbox checked={v.shareWithTrainers} onChange={(c) => setV({ ...v, shareWithTrainers: c })} label={i18n.t('profile.share_with_trainers')} />
          </div>
        )}
      </Card>
      <div className="row gap-sm">
        <AsyncButton onClick={async () => {
          const body = v.consent
            ? { consent: true, notes: v.notes ?? '', intake: v.intake, shareWithTrainers: v.shareWithTrainers, emergencyContacts: contacts }
            : { consent: false, emergencyContacts: contacts, notes: null };
          await api(`${T(gym.slug)}/me/health`, { method: 'PUT', body });
          toast.ok(i18n.t('common.saved'));
          h.reload();
        }}>{i18n.t('common.save')}</AsyncButton>
      </div>
    </div>
  );
}

function PrivacyTab({ person, onSaved }: { person: Record<string, any>; onSaved(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [v, setV] = useState({ marketingEmail: Boolean(person.marketing_email), marketingSms: Boolean(person.marketing_sms), marketingWhatsapp: Boolean(person.marketing_whatsapp), marketingPush: Boolean(person.marketing_push), photo: Boolean(person.photo_consent) });
  return (
    <div className="stack">
      <Card title={i18n.t('profile.communication')}>
        <p className="small muted">{i18n.t('profile.communication_help')}</p>
        {(['marketingEmail', 'marketingSms', 'marketingWhatsapp', 'marketingPush'] as const).map((k) => <Checkbox key={k} label={i18n.t(`consent.${k}`)} checked={v[k]} onChange={(c) => setV({ ...v, [k]: c })} />)}
        <Checkbox label={i18n.t('consent.photo')} checked={v.photo} onChange={(c) => setV({ ...v, photo: c })} />
        <AsyncButton onClick={async () => { await api(`${T(gym.slug)}/me/privacy`, { method: 'PUT', body: v }); toast.ok(i18n.t('common.saved')); onSaved(); }}>{i18n.t('common.save')}</AsyncButton>
      </Card>
      <Card title={i18n.t('profile.your_data')}>
        <p className="small muted">{i18n.t('profile.your_data_help')}</p>
        <div className="row gap-sm wrap">
          <AsyncButton variant="secondary" onClick={() => download(`${T(gym.slug)}/me/export`, `my-data-${gym.slug}.json`)}>{i18n.t('profile.export')}</AsyncButton>
          <Button variant="ghost" onClick={() => navigate('/account')}>{i18n.t('profile.delete_account')}</Button>
        </div>
      </Card>
    </div>
  );
}

function PaymentsTab() {
  const gym = useGym();
  const i18n = useI18n();
  const invoices = useApi(() => api<any[]>(`${T(gym.slug)}/me/invoices`), []);
  const payments = useApi(() => api<any[]>(`${T(gym.slug)}/me/payments`), []);
  return (
    <div className="stack">
      <Card title={i18n.t('profile.invoices')}>
        {invoices.data ? <DataTable rows={invoices.data} columns={[
          { key: 'number', label: i18n.field('number') },
          { key: 'created_at', label: i18n.field('created_at'), render: (r) => i18n.date(r.created_at) },
          { key: 'amount', label: i18n.field('amount_due_cents'), render: (r) => i18n.money(r.amount_due_cents, r.currency) },
          { key: 'status', label: i18n.field('status'), render: (r) => <Badge value={r.status} /> },
          { key: 'link', label: '', render: (r) => (r.status === 'open' && r.hosted_url ? <a className="btn btn-primary btn-sm" href={r.hosted_url}>{i18n.t('profile.pay_now')}</a> : r.pdf_url ? <a href={r.pdf_url} target="_blank" rel="noreferrer">PDF</a> : null) },
        ]} empty={i18n.t('profile.no_invoices')} /> : <Loading />}
      </Card>
      <Card title={i18n.t('profile.payments')}>
        {payments.data ? <DataTable rows={payments.data} columns={[
          { key: 'created_at', label: i18n.field('created_at'), render: (r) => i18n.date(r.paid_at ?? r.created_at) },
          { key: 'kind', label: i18n.field('kind'), render: (r) => i18n.option(r.kind) },
          { key: 'amount', label: i18n.field('amount_cents'), render: (r) => i18n.money(r.amount_cents, r.currency) },
          { key: 'refunded', label: i18n.field('refunded_cents'), render: (r) => (r.refunded_cents ? i18n.money(r.refunded_cents, r.currency) : '—') },
          { key: 'status', label: i18n.field('status'), render: (r) => <Badge value={r.status} /> },
        ]} empty={i18n.t('profile.no_payments')} /> : <Loading />}
      </Card>
    </div>
  );
}

function DocumentsTab() {
  const gym = useGym();
  const i18n = useI18n();
  const docs = useApi(() => api<any[]>(`${T(gym.slug)}/me/contracts`), []);
  const code = useApi(() => api(`${T(gym.slug)}/me/check-in`), []);
  const calendar = useApi(() => api<{ url: string }>(`${T(gym.slug)}/me/calendar`), []);
  const referrals = useApi(() => api<any>(`${T(gym.slug)}/me/referrals`), []);
  return (
    <div className="stack">
      <Card title={i18n.t('profile.documents')}>
        {docs.data ? <DataTable rows={docs.data} columns={[
          { key: 'title', label: i18n.field('title') },
          { key: 'status', label: i18n.field('status'), render: (r) => <Badge value={r.status} /> },
          { key: 'signed_at', label: i18n.field('signed_at'), render: (r) => (r.signed_at ? i18n.date(r.signed_at) : '—') },
          { key: 'open', label: '', render: (r) => <Button variant="ghost" size="sm" onClick={() => openDocument(`${T(gym.slug)}/me/contracts/${r.id}/document`)}>{i18n.t('common.view')}</Button> },
        ]} empty={i18n.t('profile.no_documents')} /> : <Loading />}
      </Card>
      {code.data?.code && (
        <Card title={i18n.t('profile.checkin_code')}>
          <p className="checkin-code">{code.data.code}</p>
          <p className="small muted">{i18n.t('profile.checkin_help')}</p>
        </Card>
      )}
      {referrals.data?.enabled && (
        <Card title={i18n.t('referral.title')}>
          <p className="small muted">{i18n.t('referral.explain', { count: referrals.data.rewardTokens, friend: referrals.data.friendRewardTokens })}</p>
          <CopyField value={referrals.data.link} />
          {referrals.data.invited.length > 0 && (
            <ul className="sets">
              {referrals.data.invited.map((x: any) => (
                <li key={x.id}>{x.name ?? i18n.t('referral.someone')} — {i18n.t(`referral.status_${x.status}`)}</li>
              ))}
            </ul>
          )}
        </Card>
      )}
      <Card title={i18n.t('profile.calendar')}>
        <p className="small muted">{i18n.t('profile.calendar_help')}</p>
        {calendar.data ? <CopyField value={calendar.data.url} /> : <span className="small muted">…</span>}
      </Card>
    </div>
  );
}

interface Product { id: string; name: string; description: string | null; category: string | null; type: string; priceCents: number; compareAtCents: number | null; images: string[]; variants: { sku: string; name?: string; price_cents?: number }[]; fulfilment: string; tokens: { name: string; quantity: number; validityDays: number | null } | null; stock: { sku: string; locationId: string; quantity: number }[] | null }

export function ShopPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const catalog = useApi(() => api<Product[]>(`/v1/public/${gym.slug}/products`), []);
  const [cart, setCart] = useState<{ productId: string; variantSku: string | null; quantity: number }[]>([]);
  const [pickup, setPickup] = useState(gym.locations[0]?.id ?? '');
  const [sku, setSku] = useState<Record<string, string>>({});
  const [code, setCode] = useState('');
  const [codeInfo, setCodeInfo] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { if (query.get('cancelled')) { toast.info(i18n.t('shop.payment_cancelled')); setQuery({ cancelled: null }); } }, []);
  const products = catalog.data ?? [];
  const byId = new Map(products.map((p) => [p.id, p]));
  const total = cart.reduce((s, c) => {
    const p = byId.get(c.productId);
    const v = p?.variants.find((x) => x.sku === c.variantSku);
    return s + (v?.price_cents ?? p?.priceCents ?? 0) * c.quantity;
  }, 0);
  const needsPickup = cart.some((c) => byId.get(c.productId)?.type === 'physical');
  const add = (p: Product): void => {
    const variantSku = p.variants.length ? sku[p.id] ?? p.variants[0].sku : null;
    setCart((x) => {
      const hit = x.find((c) => c.productId === p.id && c.variantSku === variantSku);
      return hit ? x.map((c) => (c === hit ? { ...c, quantity: c.quantity + 1 } : c)) : [...x, { productId: p.id, variantSku, quantity: 1 }];
    });
  };
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.shop')} actions={<Link to={`${gym.base}/shop/orders`}>{i18n.t('shop.my_orders')}</Link>} />
      <div className="shop">
        <div className="products">
          {!catalog.data ? <Loading /> : products.length === 0 ? <Empty>{i18n.t('shop.empty')}</Empty> : products.map((p) => (
            <div key={p.id} className="product">
              {p.images[0] ? <img src={p.images[0]} alt="" loading="lazy" /> : <div className="product-ph" aria-hidden="true">{p.tokens ? '🎟' : '🛍'}</div>}
              <h3>{p.name}</h3>
              {p.description && <p className="small muted">{p.description}</p>}
              {p.tokens && <p className="small">{i18n.t('shop.token_pack', { count: p.tokens.quantity, name: p.tokens.name, days: p.tokens.validityDays ?? '∞' })}</p>}
              <div className="row between">
                <span className="price">{i18n.money(p.priceCents)}{p.compareAtCents ? <s className="muted small"> {i18n.money(p.compareAtCents)}</s> : null}</span>
                {p.variants.length > 0 && <Select value={sku[p.id] ?? p.variants[0].sku} onChange={(e) => setSku({ ...sku, [p.id]: e.target.value })} options={p.variants.map((v) => ({ value: v.sku, label: v.name ?? v.sku }))} />}
              </div>
              <Button onClick={() => add(p)}>{i18n.t('shop.add')}</Button>
            </div>
          ))}
        </div>
        <aside className="cart card">
          <h3>{i18n.t('shop.cart')}</h3>
          {cart.length === 0 ? <p className="muted">{i18n.t('shop.cart_empty')}</p> : (
            <div className="stack-sm">
              {cart.map((c, i) => {
                const p = byId.get(c.productId);
                return (
                  <div key={i} className="row between">
                    <span>{p?.name}{c.variantSku ? ` (${p?.variants.find((v) => v.sku === c.variantSku)?.name ?? c.variantSku})` : ''}</span>
                    <span className="row gap-sm">
                      <Input type="number" min={0} max={99} style={{ width: 64 }} value={c.quantity} onChange={(e) => {
                        const q = Number(e.target.value);
                        setCart((x) => (q <= 0 ? x.filter((_, j) => j !== i) : x.map((y, j) => (j === i ? { ...y, quantity: q } : y))));
                      }} />
                    </span>
                  </div>
                );
              })}
              {needsPickup && (
                <Field label={i18n.t('shop.pickup_at')}>
                  <Select value={pickup} onChange={(e) => setPickup(e.target.value)} options={gym.locations.map((l) => ({ value: l.id, label: l.name }))} />
                </Field>
              )}
              <Field label={i18n.t('shop.code')} hint={i18n.t('shop.code_hint')}>
                <div className="row gap-sm">
                  <Input value={code} placeholder="WELCOME10" onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeInfo(null); }} />
                  <AsyncButton size="sm" variant="secondary" disabled={!code.trim()} onClick={async () => {
                    try {
                      const q = await api(`${T(gym.slug)}/discount/quote`, { body: { amountCents: total, context: 'shop', code } });
                      setCodeInfo({ ok: true, text: i18n.t('shop.code_applied', { amount: i18n.money(total - q.totalCents) }) });
                    } catch (err) {
                      setCodeInfo({ ok: false, text: errorText(err, i18n) });
                    }
                  }}>{i18n.t('shop.apply_code')}</AsyncButton>
                </div>
              </Field>
              {codeInfo && <p className={cx('small', codeInfo.ok ? 'good' : 'warn')}>{codeInfo.text}</p>}
              <div className="row between"><strong>{i18n.t('shop.total')}</strong><strong>{i18n.money(total)}</strong></div>
              <AsyncButton disabled={!gym.gym.paymentsEnabled} onClick={async () => {
                const origin = window.location.origin;
                const r = await api(`${T(gym.slug)}/shop/checkout`, { body: { items: cart, code: code || undefined, fulfilment: needsPickup ? 'pickup' : undefined, pickupLocationId: needsPickup ? pickup : undefined, successUrl: `${origin}${gym.base}/shop/orders?paid=1`, cancelUrl: `${origin}${gym.base}/shop?cancelled=1` } });
                if (r.checkoutUrl) window.location.href = r.checkoutUrl;
                else { setCart([]); navigate(`${gym.base}/shop/orders?paid=1`); }
              }}>{i18n.t('shop.checkout')}</AsyncButton>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export function OrdersPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const orders = useApi(() => api<any[]>(`${T(gym.slug)}/me/orders`), []);
  useEffect(() => { if (query.get('paid')) { toast.ok(i18n.t('shop.order_paid')); setQuery({ paid: null }); } }, []);
  const withinWithdrawal = (paidAt: string | null): boolean => Boolean(paidAt) && Date.now() - Date.parse(paidAt!) < gym.gym.policies.withdrawalDays * 86_400_000;
  return (
    <div className="page">
      <PageHeader title={i18n.t('shop.my_orders')} actions={<Link to={`${gym.base}/shop`}>← {i18n.t('nav.shop')}</Link>} />
      {!orders.data ? <Loading /> : <DataTable rows={orders.data} columns={[
        { key: 'number', label: i18n.field('number') },
        { key: 'created_at', label: i18n.field('created_at'), render: (o) => i18n.date(o.created_at) },
        { key: 'items', label: i18n.field('items'), render: (o) => (o.items ?? []).map((it: any) => `${it.quantity}× ${it.name}`).join(', ') },
        { key: 'total', label: i18n.field('total_cents'), render: (o) => i18n.money(o.total_cents, o.currency) },
        { key: 'status', label: i18n.field('status'), render: (o) => <Badge value={o.status} /> },
        { key: 'actions', label: '', render: (o) => (gym.gym.policies.withdrawalEnabled && ['paid', 'ready', 'shipped', 'completed'].includes(o.status) && withinWithdrawal(o.paid_at) ? (
          <AsyncButton variant="ghost" size="sm" confirm={i18n.t('shop.withdraw_confirm')} onClick={async () => { await api(`${T(gym.slug)}/me/orders/${o.id}/withdraw`, { body: { confirm: true } }); orders.reload(); }}>{i18n.t('shop.withdraw')}</AsyncButton>
        ) : null) },
      ]} empty={i18n.t('shop.no_orders')} />}
    </div>
  );
}

export function EventsPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const events = useApi(() => api<any[]>(`/v1/public/${gym.slug}/events`), []);
  const mine = useApi(() => api<any[]>(`${T(gym.slug)}/me/registrations`), []);
  const [open, setOpen] = useState<any | null>(null);
  const [ticket, setTicket] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [eventCode, setEventCode] = useState('');
  useEffect(() => { if (query.get('registered')) { toast.ok(i18n.t('events.registered')); setQuery({ registered: null }); } }, []);
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.events')} />
      {!events.data ? <Loading /> : events.data.length === 0 ? <Empty icon="🎟">{i18n.t('events.none')}</Empty> : (
        <div className="cards">
          {events.data.map((e) => (
            <div key={e.id} className="card event">
              {e.imageUrl && <img src={e.imageUrl} alt="" loading="lazy" />}
              <h3>{e.title}</h3>
              <p className="muted small">{i18n.dateTime(e.startsAt)}{e.location ? ` · ${e.location}` : ''}</p>
              {e.description && <p className="small">{e.description}</p>}
              <p className="small">{e.spotsLeft === null ? '' : i18n.t('events.spots_left', { count: e.spotsLeft })}</p>
              {e.myRegistration ? <Badge value={e.myRegistration.status} /> : (
                <Button onClick={() => { setOpen(e); setTicket(e.tickets.find((t: any) => !t.soldOut)?.id ?? ''); setAnswers({}); }}>{i18n.t(e.spotsLeft === 0 ? 'planner.join_waitlist' : 'events.register')}</Button>
              )}
            </div>
          ))}
        </div>
      )}
      <Card title={i18n.t('events.mine')}>
        {!mine.data ? <Loading /> : <DataTable rows={mine.data} columns={[
          { key: 'title', label: i18n.field('title') },
          { key: 'starts_at', label: i18n.field('starts_at'), render: (r) => i18n.dateTime(r.starts_at) },
          { key: 'ticket_name', label: i18n.t('events.ticket') },
          { key: 'ticket_code', label: i18n.field('ticket_code'), render: (r) => <code>{r.ticket_code}</code> },
          { key: 'status', label: i18n.field('status'), render: (r) => <Badge value={r.status} /> },
          { key: 'a', label: '', render: (r) => (['confirmed', 'waitlisted', 'pending_payment'].includes(r.status) && r.starts_at > new Date().toISOString()
            ? <AsyncButton variant="ghost" size="sm" confirm={i18n.t('events.cancel_confirm')} onClick={async () => { await api(`${T(gym.slug)}/me/registrations/${r.id}/cancel`, { body: {} }); mine.reload(); events.reload(); }}>{i18n.t('common.cancel')}</AsyncButton> : null) },
        ]} empty={i18n.t('events.no_registrations')} />}
      </Card>
      {open && (
        <Modal title={open.title} onClose={() => setOpen(null)} footer={
          <AsyncButton disabled={!ticket} onClick={async () => {
            const origin = window.location.origin;
            const r = await api(`${T(gym.slug)}/events/${open.id}/register`, { body: { ticketTypeId: ticket, answers, code: eventCode || undefined, successUrl: `${origin}${gym.base}/events?registered=1`, cancelUrl: `${origin}${gym.base}/events` } });
            if (r.checkoutUrl) { window.location.href = r.checkoutUrl; return; }
            toast.ok(i18n.t(r.registration?.status === 'waitlisted' ? 'planner.joined_waitlist' : 'events.registered'));
            setOpen(null); events.reload(); mine.reload();
          }}>{i18n.t('events.register')}</AsyncButton>
        }>
          <div className="stack">
            <Field label={i18n.t('shop.code')} hint={i18n.t('shop.code_hint')}>
              <Input value={eventCode} placeholder="WELCOME10" onChange={(e) => setEventCode(e.target.value.toUpperCase())} />
            </Field>
            <Field label={i18n.t('events.ticket')}>
              <Select value={ticket} onChange={(e) => setTicket(e.target.value)} options={open.tickets.map((t: any) => ({ value: t.id, label: `${t.name} · ${t.tokensRequired ? `${t.tokensRequired} token` : t.priceCents ? i18n.money(t.priceCents) : i18n.t('events.free')}${t.soldOut ? ` (${i18n.t('planner.full')})` : ''}` }))} />
            </Field>
            {(open.questions ?? []).map((q: any, i: number) => {
              const label = typeof q === 'string' ? q : q.label;
              return <Field key={i} label={label}><Input value={answers[label] ?? ''} onChange={(e) => setAnswers({ ...answers, [label]: e.target.value })} /></Field>;
            })}
            {open.cancellationPolicy && <p className="small muted">{open.cancellationPolicy}</p>}
          </div>
        </Modal>
      )}
    </div>
  );
}

export function MemberChatPage() {
  const i18n = useI18n();
  return (
    <div className="page narrow">
      <PageHeader title={i18n.t('chat.title')} />
      <ChatBox className="chat-page" />
    </div>
  );
}
